/**
 * Word Collection AI Context Provider
 *
 * Builds AI context and evaluates triggers to generate feedback prompts.
 * Feedback is generated in the background but the panel is USER-CONTROLLED.
 * The AI never auto-pops up - user opens the panel when they want insights.
 */

import { createContext, useContext, useMemo, useRef, useCallback, useState, useEffect, type ReactNode } from 'react'
import { useWordCollectionStore } from '../store/wordCollectionStore'
import { buildAIContext } from '../utils/wordCollection/aiContext'
import { findActiveTrigger, FEEDBACK_LIMITS, type FeedbackTrigger } from '../services/ai/wordCollectionTriggers'
import { useDebounce } from '../hooks/useDebounce'
import type { WordCollectionAIContext } from '../types/wordCollectionAIContext'
import type { AIFeedbackItem } from '../components/debug'
import type { AIFeedbackPreferences } from '../types/song'

/**
 * Context state shape
 */
interface WordCollectionAIContextState {
  /** Current AI context (memoized) */
  context: WordCollectionAIContext | null
  /** Active feedback item (null if none) */
  feedback: AIFeedbackItem | null
  /** Is feedback being generated? */
  isLoading: boolean
  /** Dismiss current feedback */
  dismiss: () => void
}

const WordCollectionAIContextInstance = createContext<WordCollectionAIContextState | null>(null)

/**
 * Provider props
 */
interface WordCollectionAIProviderProps {
  children: ReactNode
  /** Optional lyrics text for alignment feedback */
  lyrics?: string
  /** Optional stability level (0-100) */
  stabilityLevel?: number
  /** Optional AI feedback preferences from song settings */
  aiFeedbackPreferences?: AIFeedbackPreferences
}

// Dismissal persistence REMOVED - AI should never be proactive
// No localStorage needed since analysis is user-initiated only

/**
 * Word Collection AI Provider - evaluates triggers and delivers feedback
 */
export function WordCollectionAIProvider({
  children,
  lyrics,
  stabilityLevel,
  aiFeedbackPreferences
}: WordCollectionAIProviderProps) {
  // Get word collection state from Zustand store
  const words = useWordCollectionStore(state => state.words)
  const usedWordsInLyrics = useWordCollectionStore(state => state.usedWordsInLyrics)
  const storeIsLoading = useWordCollectionStore(state => state.isLoading)

  // Feedback state
  const [feedback, setFeedback] = useState<AIFeedbackItem | null>(null)
  // Note: storeIsLoading tracks the word collection load state, not local feedback generation

  // Cooldown tracking - trigger ID -> last triggered timestamp
  const cooldownStateRef = useRef<Map<string, number>>(new Map())
  const lastGlobalTriggerRef = useRef<number>(0)

  // Build AI context (memoized for stability)
  const context = useMemo(() => {
    return buildAIContext(words, usedWordsInLyrics, lyrics, stabilityLevel, aiFeedbackPreferences)
  }, [words, usedWordsInLyrics, lyrics, stabilityLevel, aiFeedbackPreferences])

  // Debounce context changes (500ms) to prevent excessive trigger evaluation
  const debouncedContext = useDebounce(context, 500)

  // Clear feedback when song changes (store starts loading new data)
  // This prevents stale feedback from previous song from showing
  useEffect(() => {
    if (storeIsLoading) {
      setFeedback(null)
      // Reset cooldowns for fresh start with new song
      cooldownStateRef.current.clear()
      lastGlobalTriggerRef.current = 0
    }
  }, [storeIsLoading])

  // Evaluate triggers when debounced context changes
  // Feedback is generated but panel is USER-CONTROLLED (doesn't auto-show)
  useEffect(() => {
    // Guard: Don't generate feedback while store is loading new song data
    // This prevents the race condition where old data is used before new data arrives
    if (storeIsLoading) {
      return
    }

    const now = Date.now()

    // Check global rate limit
    const timeSinceLastGlobal = now - lastGlobalTriggerRef.current
    if (timeSinceLastGlobal < FEEDBACK_LIMITS.minTimeBetweenAnyMs) {
      return // Too soon since last trigger
    }

    // Find active trigger
    const activeTrigger = findActiveTrigger(debouncedContext, cooldownStateRef.current)

    if (!activeTrigger) {
      // No active trigger - clear feedback if present
      setFeedback(null)
      return
    }

    // Generate feedback from trigger (displayed when user opens panel)
    const feedbackItem = generateFeedbackItem(activeTrigger, debouncedContext)
    setFeedback(feedbackItem)

    // Update cooldown state
    cooldownStateRef.current.set(activeTrigger.id, now)
    lastGlobalTriggerRef.current = now
  }, [debouncedContext, storeIsLoading])

  // Simple dismiss handler - no persistence needed
  const dismiss = useCallback(() => {
    setFeedback(null)
  }, [])

  const value = useMemo(() => ({
    context,
    feedback,
    isLoading: storeIsLoading,
    dismiss
  }), [context, feedback, storeIsLoading, dismiss])

  return (
    <WordCollectionAIContextInstance.Provider value={value}>
      {children}
    </WordCollectionAIContextInstance.Provider>
  )
}

/**
 * Hook to consume word collection AI context (internal use only)
 * Public hook is exported from hooks/useWordCollectionAIFeedback.ts
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useWordCollectionAIContext(): WordCollectionAIContextState {
  const context = useContext(WordCollectionAIContextInstance)

  if (!context) {
    throw new Error('useWordCollectionAIContext must be used within WordCollectionAIProvider')
  }

  return context
}

/**
 * Helper: Generate AIFeedbackItem from trigger and context
 */
function generateFeedbackItem(
  trigger: FeedbackTrigger,
  context: WordCollectionAIContext
): AIFeedbackItem {
  const prompt = trigger.buildPrompt(context)

  return {
    id: trigger.id,
    line: 0, // Word collection feedback not line-specific
    type: 'discovery', // Word collection insights are discovery type
    category: 'Word Collection',
    title: trigger.name,
    description: prompt,
    pathways: [], // Could be populated with specific action suggestions
    location: 'Word Collection'
  }
}
